import { arrivedSectionIds, headlineScores, missingSectionIds } from "./derive";
import type {
  XFactorBriefingDetail,
  XFactorDigestData,
} from "../../../Types/XFactorBriefing";
import type {
  XFactorHeadlineScores,
  XFactorPortfolioAccount,
} from "../../../Types/XFactorViewsRendering";

const EMPTY_SCORES: XFactorHeadlineScores = {
  overall: null,
  health: null,
  fit: null,
  opportunity: null,
  risk: null,
};

const isPlaceholderAccountName = (name?: string | null) => {
  const n = String(name || "")
    .trim()
    .toLowerCase();
  return !n || n === "unknown" || n === "unknown account";
};

/** Placeholder row key for EB accounts that have no xfactor briefing yet. */
export const pendingDigestId = (accountId: string) => `pending:${accountId}`;

export const isPendingDigestId = (digestId?: string | null) =>
  Boolean(digestId && String(digestId).startsWith("pending:"));

export type XFactorWatchListResponse = {
  success?: boolean;
  date?: string;
  is_previous_week?: boolean;
  total_accounts?: number;
  organization_id?: string;
  results?: Array<{
    account_id: string;
    customer_name?: string;
    success?: boolean;
    briefing?: {
      account_id: string;
      account_name?: string;
      date?: string;
      organization_id?: string;
      data?: XFactorDigestData;
    } | null;
    error?: string;
  }>;
};

export type MappedWatchListPayload = {
  data: XFactorBriefingDetail | null;
  loading: boolean;
  error: string | null;
};

export type XFactorAccountBriefingsResponse = {
  success?: boolean;
  total_accounts?: number;
  accounts?: Array<{
    customer_id: string;
    total?: number;
    briefings?: Array<{
      digest_id?: string;
      customer_id?: string;
      account_name?: string;
      digest_date?: string | null;
      data?: XFactorDigestData;
      section_ids?: string[];
      section_count?: number;
      is_partial?: boolean;
      missing_section_ids?: string[];
    }>;
  }>;
};

export const isStaleDigest = (
  digestDate: string | null | undefined,
  freshnessDays: number
): boolean => {
  if (!digestDate) return true;
  const parsed = new Date(digestDate);
  if (Number.isNaN(parsed.getTime())) return true;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - Math.max(0, freshnessDays));
  cutoff.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  return parsed < cutoff;
};

/** Map watch-list-briefing response → portfolio rows + matrix payloads (one upstream call). */
export const mapWatchListToViews = (
  response: XFactorWatchListResponse | null | undefined,
  freshnessDays: number
): {
  accounts: XFactorPortfolioAccount[];
  payloads: Record<string, MappedWatchListPayload>;
  briefingDate: string | null;
} => {
  const accounts: XFactorPortfolioAccount[] = [];
  const payloads: Record<string, MappedWatchListPayload> = {};

  for (const row of response?.results || []) {
    const accountId = String(row.account_id || "").trim();
    if (!accountId) continue;

    const briefing = row.briefing;
    const data = briefing?.data;
    const hasPayload = Boolean(data && typeof data === "object");
    const displayName =
      (!isPlaceholderAccountName(briefing?.account_name) &&
        briefing?.account_name) ||
      row.customer_name ||
      "Unknown account";

    if (hasPayload && briefing) {
      const sectionIds = arrivedSectionIds(data);
      const missingIds = missingSectionIds(sectionIds);
      const scores = headlineScores(data);
      const digestDate = briefing.date ?? response?.date ?? null;

      accounts.push({
        digest_id: accountId,
        digest_date: digestDate,
        account_id: accountId,
        account_name: displayName,
        section_count: sectionIds.length,
        is_partial: missingIds.length > 0,
        missing_section_ids: missingIds,
        has_payload: true,
        is_stale: isStaleDigest(digestDate, freshnessDays),
        scores,
      });

      payloads[accountId] = {
        data: {
          digest_id: accountId,
          account_name: displayName,
          digest_date: digestDate,
          data,
          section_ids: sectionIds,
          section_count: sectionIds.length,
          is_partial: missingIds.length > 0,
          missing_section_ids: missingIds,
        } as XFactorBriefingDetail,
        loading: false,
        error: null,
      };
    } else {
      accounts.push({
        digest_id: pendingDigestId(accountId),
        digest_date: null,
        account_id: accountId,
        account_name: row.customer_name || "Unknown account",
        section_count: 0,
        is_partial: false,
        has_payload: false,
        is_stale: false,
        scores: EMPTY_SCORES,
      });
    }
  }

  accounts.sort((a, b) => {
    const aOverall = a.scores.overall;
    const bOverall = b.scores.overall;
    if (aOverall == null && bOverall == null) {
      return (a.account_name || "").localeCompare(b.account_name || "");
    }
    if (aOverall == null) return 1;
    if (bOverall == null) return -1;
    return (
      bOverall - aOverall ||
      (a.account_name || "").localeCompare(b.account_name || "")
    );
  });

  return {
    accounts,
    payloads,
    briefingDate: response?.date ?? null,
  };
};

/** Map POST /account-briefings → matrix rows (one row per requested EB account id). */
export const mapAccountBriefingsToViews = (
  response: XFactorAccountBriefingsResponse | null | undefined,
  freshnessDays: number,
  orderedAccountIds: string[],
  accountNames: Record<string, string | null | undefined> = {}
): {
  accounts: XFactorPortfolioAccount[];
  payloads: Record<string, MappedWatchListPayload>;
  briefingDate: string | null;
} => {
  const byCustomerId = new Map<
    string,
    NonNullable<XFactorAccountBriefingsResponse["accounts"]>[number]
  >();
  for (const row of response?.accounts || []) {
    const cid = String(row.customer_id || "").trim();
    if (cid && !byCustomerId.has(cid)) {
      byCustomerId.set(cid, row);
    }
  }

  const accounts: XFactorPortfolioAccount[] = [];
  const payloads: Record<string, MappedWatchListPayload> = {};
  let briefingDate: string | null = null;

  for (const accountId of orderedAccountIds) {
    const cid = String(accountId || "").trim();
    if (!cid) continue;

    const row = byCustomerId.get(cid);
    const briefing = row?.briefings?.[0];
    const data = briefing?.data;
    const hasPayload = Boolean(data && typeof data === "object");
    const displayName =
      (!isPlaceholderAccountName(briefing?.account_name) &&
        briefing?.account_name) ||
      accountNames[cid] ||
      "Unknown account";

    if (hasPayload && briefing) {
      const sectionIds =
        briefing.section_ids && briefing.section_ids.length
          ? briefing.section_ids
          : arrivedSectionIds(data);
      const missingIds = Array.isArray(briefing.missing_section_ids)
        ? briefing.missing_section_ids
        : missingSectionIds(sectionIds);
      const scores = headlineScores(data);
      const digestDate = briefing.digest_date ?? null;
      if (digestDate && !briefingDate) briefingDate = digestDate;
      const isPartial = missingIds.length > 0 || Boolean(briefing.is_partial);

      accounts.push({
        digest_id: cid,
        digest_date: digestDate,
        account_id: cid,
        account_name: displayName,
        section_count: sectionIds.length,
        is_partial: isPartial,
        missing_section_ids: missingIds,
        has_payload: true,
        is_stale: isStaleDigest(digestDate, freshnessDays),
        scores,
      });

      payloads[cid] = {
        data: {
          digest_id: briefing.digest_id || cid,
          account_name: displayName,
          customer_id: cid,
          digest_date: digestDate,
          data,
          section_ids: sectionIds,
          section_count: sectionIds.length,
          is_partial: isPartial,
          missing_section_ids: missingIds,
        } as XFactorBriefingDetail,
        loading: false,
        error: null,
      };
    } else {
      accounts.push({
        digest_id: pendingDigestId(cid),
        digest_date: null,
        account_id: cid,
        account_name: accountNames[cid] || "Unknown account",
        section_count: 0,
        is_partial: false,
        has_payload: false,
        is_stale: false,
        scores: EMPTY_SCORES,
      });
    }
  }

  return { accounts, payloads, briefingDate };
};
